import { useEffect, useRef, useState } from 'react'
import styled from 'styled-components'

import { COLORS } from '../../commons/Colors'
import { UserOption } from '../API/FeatureFlagsAPI'

type Props = {
    label: string
    helperText?: string
    allUsers: UserOption[]
    selectedIds: string[]
    onChange: (ids: string[]) => void
}

export default function UserListSelector({
    label,
    helperText,
    allUsers,
    selectedIds,
    onChange,
}: Props) {
    const [filter, setFilter] = useState('')
    const selectAllRef = useRef<HTMLInputElement>(null)

    const selectedSet = new Set(selectedIds)

    const knownIds = new Set(allUsers.map((u) => u.id))
    const unknownSelected: UserOption[] = selectedIds
        .filter((id) => !knownIds.has(id))
        .map((id) => ({ id, name: id, legacyCode: '' }))
    const allRows = [...allUsers, ...unknownSelected]

    const filtered = allRows.filter((u) => {
        const q = filter.toLowerCase()
        return (
            (u.name ?? '').toLowerCase().includes(q) ||
            (u.legacyCode ?? '').toLowerCase().includes(q)
        )
    })

    const selectedFiltered = filtered.filter((u) => selectedSet.has(u.id))
    const unselectedFiltered = filtered.filter((u) => !selectedSet.has(u.id))

    const allSelected =
        allRows.length > 0 && allRows.every((u) => selectedSet.has(u.id))
    const someSelected = selectedIds.length > 0 && !allSelected

    useEffect(() => {
        if (selectAllRef.current) {
            selectAllRef.current.indeterminate = someSelected
        }
    }, [someSelected])

    const handleToggle = (user: UserOption) => {
        if (selectedSet.has(user.id)) {
            onChange(selectedIds.filter((id) => id !== user.id))
        } else {
            onChange([...selectedIds, user.id])
        }
    }

    const handleToggleAll = () => {
        if (allSelected) {
            onChange([])
        } else {
            onChange(allRows.map((u) => u.id))
        }
    }

    return (
        <Container>
            <Label>{label}</Label>
            {helperText && <HelperText>{helperText}</HelperText>}

            <FilterInput
                type="text"
                placeholder="Buscar por nome ou código..."
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
            />

            <SelectAllRow onClick={handleToggleAll}>
                <Checkbox
                    ref={selectAllRef}
                    readOnly
                    checked={allSelected}
                    disabled={allRows.length === 0}
                />
                <SelectAllLabel>Selecionar todos</SelectAllLabel>
            </SelectAllRow>

            <ListBox>
                {filtered.length === 0 && (
                    <EmptyRow>Nenhum usuário encontrado</EmptyRow>
                )}
                {selectedFiltered.length > 0 && (
                    <>
                        {selectedFiltered.map((user) => (
                            <Row
                                key={user.id}
                                selected
                                onClick={() => handleToggle(user)}
                            >
                                <Checkbox readOnly checked />
                                <UserName>{user.name}</UserName>
                                {user.legacyCode && (
                                    <LegacyCode>{user.legacyCode}</LegacyCode>
                                )}
                            </Row>
                        ))}
                        {unselectedFiltered.length > 0 && <Divider />}
                    </>
                )}
                {unselectedFiltered.map((user) => (
                    <Row
                        key={user.id}
                        selected={false}
                        onClick={() => handleToggle(user)}
                    >
                        <Checkbox readOnly checked={false} />
                        <UserName>{user.name}</UserName>
                        {user.legacyCode && (
                            <LegacyCode>{user.legacyCode}</LegacyCode>
                        )}
                    </Row>
                ))}
            </ListBox>

            {selectedIds.length > 0 && (
                <SelectedCount>
                    {selectedIds.length} selecionado
                    {selectedIds.length > 1 ? 's' : ''}
                </SelectedCount>
            )}
        </Container>
    )
}

const Container = styled.div``

const Label = styled.label`
    display: block;
    font-size: 13px;
    font-weight: bold;
    color: ${COLORS.primaryGray};
    margin-bottom: 4px;
`

const HelperText = styled.span`
    display: block;
    font-size: 11px;
    color: #999;
    margin-bottom: 8px;
`

const FilterInput = styled.input`
    width: 100%;
    box-sizing: border-box;
    border: 1px solid ${COLORS.borderGray};
    border-radius: 4px 4px 0 0;
    padding: 8px 10px;
    font-size: 13px;
    font-family: Roboto;
    color: ${COLORS.primaryGray};
    outline: none;

    &:focus {
        border-color: #bbb;
        box-shadow: 0 0 0 2px rgba(0, 0, 0, 0.05);
    }

    &::placeholder {
        color: #aaa;
    }
`

const SelectAllRow = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 7px 10px;
    cursor: pointer;
    background: #fafafa;
    border: 1px solid ${COLORS.borderGray};
    border-top: none;
    font-size: 13px;

    &:hover {
        background: #f0f0f0;
    }
`

const SelectAllLabel = styled.span`
    font-weight: bold;
    color: ${COLORS.primaryGray};
`

const ListBox = styled.div`
    border: 1px solid ${COLORS.borderGray};
    border-top: none;
    border-radius: 0 0 4px 4px;
    max-height: 180px;
    overflow-y: auto;
    background: white;
`

const Row = styled.div<{ selected: boolean }>`
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 7px 10px;
    cursor: pointer;
    background: ${({ selected }) => (selected ? '#e8f4fd' : 'white')};
    border-bottom: 1px solid #f0f0f0;
    font-size: 13px;

    &:last-child {
        border-bottom: none;
    }

    &:hover {
        background: ${({ selected }) => (selected ? '#d6ecf8' : '#f9f9f9')};
    }
`

const Checkbox = styled.input.attrs({ type: 'checkbox' })`
    cursor: pointer;
    flex-shrink: 0;
`

const UserName = styled.span`
    flex: 1;
    color: ${COLORS.primaryGray};
`

const LegacyCode = styled.span`
    font-size: 11px;
    color: #999;
`

const Divider = styled.div`
    height: 1px;
    background: #c8e6f8;
    margin: 2px 0;
`

const EmptyRow = styled.div`
    padding: 12px 10px;
    font-size: 13px;
    color: #999;
    text-align: center;
`

const SelectedCount = styled.span`
    display: block;
    font-size: 11px;
    color: #1976d2;
    margin-top: 4px;
`
